/**
 * What to declare a bill file as when uploading it.
 *
 * The presigned upload binds a Content-Type into the signature and the service checks it against
 * its allowed list before issuing anything, so an upload has to name a type up front (LB-5898). The
 * multipart path it replaces did not: it validated by magic number once it had the bytes, so a file
 * the browser reported no type for still went up fine. This keeps that true.
 */

/** Mirrors BillFileValidator's allowed extensions, and `ACCEPTED_BILL_FILE_TYPES` on the picker. */
const CONTENT_TYPE_BY_EXTENSION = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp'
};

/**
 * Every type the service will issue an upload URL for.
 *
 * Deduplicated because two extensions map to one type: `.jpg` and `.jpeg` are both `image/jpeg`,
 * and a list that named it twice would read as though we accepted five kinds of file and six types.
 */
export const ACCEPTED_BILL_CONTENT_TYPES = Array.from(new Set(Object.values(CONTENT_TYPE_BY_EXTENSION)));

/**
 * The type to upload this file as, or `''` when it is not one the service takes.
 *
 * The browser's own answer is preferred when it is one of the allowed types, because it read the
 * file and the name is only a claim about it. An empty or unrecognised `type` falls back to the
 * extension — browsers routinely report nothing for `.webp`, and a file dragged from an archive can
 * arrive with no type at all. Returning `''` rather than guessing a default is deliberate: the
 * service would refuse the guess with a 400, and "we do not take this kind of file" is something the
 * page can say before anything leaves the browser.
 */
export function billContentTypeOf(file) {
  const declared = ((file === null || file === void 0 ? void 0 : file.type) || '').toLowerCase().trim();
  if (ACCEPTED_BILL_CONTENT_TYPES.includes(declared)) return declared;
  const name = (file === null || file === void 0 ? void 0 : file.name) || '';
  const dot = name.lastIndexOf('.');
  // No dot, or a name that is nothing but one, carries no extension to fall back on.
  if (dot < 1) return '';
  return CONTENT_TYPE_BY_EXTENSION[name.slice(dot + 1).toLowerCase()] || '';
}